import { StyledIconBase } from "@styled-icons/styled-icon";
import { Button } from "components/primitives";
import { DashboardContext } from "contexts/DashboardContext";
import { useContext } from "react";
import styled from "styled-components";
import { ButtonProps } from "types/ButtonProps";

import { AllWidgetTypes } from "../widgets";

interface Props {
  widgetCounts: { [widgetType: string]: number };
  addWidget: (typeId: string, dashboardId: string) => void;
}

interface ToolbarButtonProps extends ButtonProps {
  children?: React.ReactNode;
  widgetCount: number;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ children, widgetCount, ...props }) => {
  return (
    <ToolbarButtonContainer {...props}>
      {children}
      {widgetCount > 0 && (
        <ToolbarButtonCounter>
          <ToolbarButtonCountText>{widgetCount}</ToolbarButtonCountText>
        </ToolbarButtonCounter>
      )}
    </ToolbarButtonContainer>
  );
};

export const Toolbar: React.FC<Props> = ({ widgetCounts, addWidget }) => {
  const { data: globalData } = useContext(DashboardContext);

  const allWidgetsArray = Object.entries(AllWidgetTypes)?.toSorted((a, b) =>
    a[1].name.localeCompare(b[1].name),
  );
  const toolbarWidgets = Object.fromEntries(
    allWidgetsArray.filter(([_, value]) => value.showOnToolbar),
  );

  return (
    <OuterContainer>
      <HeaderText>Widgets</HeaderText>
      <InnerContainer>
        {Object.keys(toolbarWidgets).map((widgetTypeId, index) => {
          const Icon = toolbarWidgets[widgetTypeId].icon;

          const handleCreateNewWidget = () => {
            addWidget(widgetTypeId, globalData.activeDashboardId);
          };

          return (
            <WidgetButton
              widgetCount={widgetCounts[widgetTypeId] || 0}
              onClick={handleCreateNewWidget}
              style={{ order: index + 1 }}
              key={widgetTypeId}
            >
              {Icon && <Icon width={40} />}
              <ToolbarButtonText>{toolbarWidgets[widgetTypeId].name}</ToolbarButtonText>
            </WidgetButton>
          );
        })}
      </InnerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  padding: 5px;
  overflow: auto;
  width: 100%;
`;

const InnerContainer = styled.div`
  display: flex;
  gap: 10px;
  margin: 5px 0;
  padding: 5px 0;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ToolbarButtonContainer = styled(Button)`
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 5px rgba(0, 9, 43, 0.5);
  border-radius: 8px;
  border: none;
  color: var(--secondary);
  font-weight: 600;

  ${StyledIconBase} {
    position: absolute;
    left: 50%;
    top: 37%;
    transform: translate(-50%, -50%);
  }
`;

const ToolbarButtonText = styled.p`
  position: absolute;
  left: 50%;
  bottom: 22%;
  transform: translate(-50%, 50%);
  width: 100%;
  font-size: 9pt;
  font-weight: 600;
  line-height: 1.2em;
`;

const ToolbarButtonCountText = styled.p`
  margin-top: -3px;
`;

const ToolbarButtonCounter = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  border-radius: 50%;
  background-color: var(--primary);
  border: 1px solid var(--primary);
  width: 20px;
  height: 20px;
  color: black;
`;

const WidgetButton = styled(ToolbarButton).attrs({
  variant: "outline-primary",
})`
  width: 47%;
  aspect-ratio: 1;
`;

const HeaderText = styled.p`
  color: var(--secondary);
  font-weight: 600;
  font-size: 1.33em;
`;
